import { logClientError } from 'src/util/server-log';
import { Injectable } from "@angular/core";
import { CanActivate } from "@angular/router";
import { SharedService } from "@services/shared/shared.service";
import { GrpcSubscriptionService } from "@services/grpc-subscription/grpc-subscription.service";
import { GrpcTokenizerService } from "@services/grpc-tokenizer/grpc-tokenizer.service";
import { from, Observable, of, forkJoin } from "rxjs";
import { map, catchError, switchMap } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class ProfileGuard implements CanActivate {
  constructor(
    private shared: SharedService,
    private grpcSubscription: GrpcSubscriptionService,
    private grpcTokenizer: GrpcTokenizerService,
  ) {}

  canActivate(): Observable<boolean> {
    // When entering the profile, the checkout planId is no longer relevant.
    this.shared.setPlanId(null);

    // If the subscription is already loaded, we don't need to do anything else.
    if (this.shared.currentSubscription) {
      return of(true);
    }

    // Fetch the user's current subscription, plan, and default payment method.
    return from(this.grpcSubscription.getCurrentSubscription()).pipe(
      switchMap((subscriptionResponse) => {
        const subscription = subscriptionResponse.toObject().subscription;
        this.shared.currentSubscription = subscription;
        const plan$ =
          subscription?.plan?.id && !this.shared.currentPlan
            ? from(this.grpcSubscription.getPlanById(subscription.plan.id)).pipe(
                map((planResponse) => {
                  this.shared.currentPlan = (planResponse as any).toObject().plan as any;
                  return true;
                }),
                catchError((error) => {
                  logClientError("Error getting plan by ID:", error);
                  return of(true);
                }),
              )
            : of(true);

        // Fetch active subscription directly from GetActiveSubscription endpoint
        const activeSubscription$ = from(this.grpcSubscription.getActiveSubscription()).pipe(
              switchMap((response) => {
                const activeSubscription = response.toObject().subscription || null;
                this.shared.activeSubscription = activeSubscription;
                // Fetch plan for active subscription
                if (activeSubscription?.plan?.id) {
                  return from(this.grpcSubscription.getPlanById(activeSubscription.plan.id)).pipe(
                    map((planResponse) => {
                      this.shared.activePlan = (planResponse as any).toObject().plan as any;
                      return true;
                    }),
                    catchError((error) => {
                      logClientError("Error getting active plan by ID:", error);
                      return of(true);
                    }),
                  );
                }
                return of(true);
              }),
              catchError((error) => {
                logClientError("Error getting active subscription:", error);
                return of(true);
              }),
            );

        const defaultPayment$ = !this.shared.defaultPayment
          ? from(this.grpcSubscription.getDefaultPayment()).pipe(
              map((response) => {
                this.shared.defaultPayment = response.toObject();
                return true;
              }),
              catchError((error) => {
                logClientError("Error getting default payment:", error);
                return of(true);
              }),
            )
          : of(true);

        const wallet$ = !this.shared.wallet
          ? from(this.grpcTokenizer.getWallet(this.shared.user?.sub ?? "")).pipe(
              map((response) => {
                this.shared.wallet = response.toObject();
                return true;
              }),
              catchError((error) => {
                logClientError("Error getting wallet:", error);
                return of(true);
              }),
            )
          : of(true);

        return forkJoin([plan$, activeSubscription$, defaultPayment$, wallet$]).pipe(map(() => true));
      }),
      catchError((error) => {
        logClientError("Error in ProfileGuard:", error);
        // In case of error, we still allow access to the profile, but data might be missing.
        return of(true);
      }),
    );
  }
}
